import React from 'react';
import {Body, Button, Icon} from '@lightricks/react-design-system';
import CircleLoader from '@/components/circle-loader';
import {DeliverableSlot} from '../../types';
import {
  type ProcessingPreview,
  useProcessingPreviewStore,
} from '../../processingPreviewStore';
import styles from './SlotRow.module.scss';

const STATE_LABEL: Record<DeliverableSlot['state'], string> = {
  empty: 'Required',
  processing: 'Processing',
  pending: 'In review',
  approved: 'Approved',
  edits_requested: 'Edits requested',
  archived: 'Archived',
};

interface Props {
  slot: DeliverableSlot;
  groupLabel: string;
  onAction: (slot: DeliverableSlot) => void;
}

function UploadIcon() {
  return (
    <svg
      aria-hidden="true"
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M8 2L8 11M8 2L5 5M8 2L11 5"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M2 13H14"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
      />
    </svg>
  );
}

function SlotThumbnail({
  slot,
  localPreview,
}: {
  slot: DeliverableSlot;
  localPreview?: ProcessingPreview;
}) {
  if (slot.submission?.thumbnailUrl) {
    return <img src={slot.submission.thumbnailUrl} alt="" />;
  }
  if (localPreview?.mediaType === 'image') {
    return <img src={localPreview.url} alt="" data-testid="local-preview" />;
  }
  if (localPreview?.mediaType === 'video') {
    return (
      <video
        src={localPreview.url}
        muted
        playsInline
        data-testid="local-preview"
      >
        <track kind="captions" />
      </video>
    );
  }
  if (slot.state === 'processing') {
    return null;
  }
  return (
    <Icon
      name={slot.isMixedMedia ? 'Navigation-Grid' : 'Actions-Add-Normal'}
      size="medium"
      appearance="secondary"
    />
  );
}

export default function SlotRow({slot, groupLabel, onAction}: Props) {
  const viewable = slot.state === 'pending' || slot.state === 'approved';
  const submissionId = slot.submission?.id;
  const localPreview = useProcessingPreviewStore((state) =>
    submissionId ? state.bySubmissionId[submissionId] : undefined
  );

  const body = (
    <>
      <div className={styles.thumb} aria-hidden>
        <SlotThumbnail slot={slot} localPreview={localPreview} />
        {slot.state === 'processing' && (
          <div
            className={styles.processingIndicator}
            data-testid="processing-indicator"
          >
            <CircleLoader size="small" />
          </div>
        )}
      </div>
      <div className={styles.main}>
        <Body size="sm">{`${groupLabel} · ${slot.index}`}</Body>
        <div className={styles.stateRow}>
          <span
            className={`${styles.pill} ${styles[`pill_${slot.state}`]}`}
            data-testid="slot-pill"
          >
            {STATE_LABEL[slot.state]}
          </span>
        </div>
        {slot.state === 'edits_requested' && slot.submission?.brandMessage && (
          <Body
            size="xs"
            className={styles.feedback}
          >{`"${slot.submission.brandMessage}"`}</Body>
        )}
      </div>
    </>
  );

  if (!viewable) {
    return (
      <div
        className={styles.slotRow}
        data-testid="slot-row"
        data-state={slot.state}
      >
        {body}
        {(slot.state === 'empty' || slot.state === 'edits_requested') && (
          <Button
            testID="slot-action"
            appearance="brand"
            mode="filled"
            size="medium"
            onClick={() => onAction(slot)}
          >
            {slot.state === 'edits_requested' ? (
              'Revise'
            ) : (
              <>
                <UploadIcon />
                Upload
              </>
            )}
          </Button>
        )}
      </div>
    );
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      if (e.key === ' ') e.preventDefault();
      onAction(slot);
    }
  };

  return (
    <div
      className={styles.slotRow}
      data-testid="slot-row"
      data-state={slot.state}
      role="button"
      tabIndex={0}
      onClick={() => onAction(slot)}
      onKeyDown={handleKeyDown}
    >
      {body}
      <Button
        testID="slot-view"
        appearance="neutral"
        mode="plain"
        size="medium"
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          onAction(slot);
        }}
      >
        View
      </Button>
    </div>
  );
}
